/*
 * Polyfills
 */

import Es6Promise from 'es6-promise'

/*
 * Vue Core Imports
 */
import Vue from 'vue'
import Vuex from 'vuex'
import App from './App'

import router from './router'
import store, { resetState } from './store'

import VuejsDialog from 'vuejs-dialog'
import 'vuejs-dialog/dist/vuejs-dialog.min.css'

/*
 * Vue-Resource Import
 * https://github.com/pagekit/vue-resource
 * Note: We opted for Vue-Resource instead of Axios because of an issue with Vue-Auth
 */
import VueResource from 'vue-resource'

/*
 * Vue Auth Imports
 * https://github.com/websanova/vue-auth
 */
import vueAuth from '@websanova/vue-auth'
import vueAuthBearer from '@websanova/vue-auth/drivers/auth/bearer.js'
import vueAuthHttp from '@websanova/vue-auth/drivers/http/vue-resource.1.x.js'
import vueAuthRouter from '@websanova/vue-auth/drivers/router/vue-router.2.x.js'

/* **************************
 * Plugin & Library Imports *
 * **************************/

/*
 * Public
 */

// https://github.com/sagalbot/vue-select
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import BootstrapVue from 'bootstrap-vue'

// https://github.com/brockpetrie/vue-moment
import VueMoment from 'vue-moment'

import PrettyCheckbox from 'pretty-checkbox-vue'

import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

// https://github.com/baianat/vee-validate
import VeeValidate from 'vee-validate'

//  https://lodash.com/docs/4.17.10
import _ from 'lodash'

// https://github.com/charliekassel/vuejs-datepicker
import DatePicker from 'vuejs-datepicker'

// https://github.com/Innologica/vue2-daterange-picker
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

// https://github.com/OneWayTech/vue2-datatable
import Datatable from 'vue2-datatable-component'

// https://github.com/xkjyeah/vue-google-maps#readme
import * as VueGoogleMaps from 'vue2-google-maps'

import InformationOutline from 'vue-material-design-icons/InformationOutline.vue'
import AccountOutline from 'vue-material-design-icons/AccountOutline.vue'
import ArrowDown from 'vue-material-design-icons/ArrowDown.vue'
// import InformationOutline from 'vue-material-design-icons/InformationOutline.vue'
// import InformationOutline from 'vue-material-design-icons/InformationOutline.vue'

/*
 * Custom
 */
import utils from './utils'
import endpoints from './api/endpoints'

/* *********************************
 * Polyfills *
 * *********************************/
Es6Promise.polyfill()

/* *********************************
 * Plugin & Library Initialization *
 * *********************************/

/*
 * Global Components
 */
Vue.component('datepicker', DatePicker)
Vue.component('date-range-picker', DateRangePicker)
Vue.component('pulse-loader', PulseLoader)
Vue.component('v-select', vSelect)
Vue.component('info-icon', InformationOutline)
Vue.component('account-icon', AccountOutline)
Vue.component('arrow-down', ArrowDown)
// Vue.component('info-icon', InformationOutline)
// Vue.component('info-icon', InformationOutline)

/*
 * Plugin Initializaiton
 */
Vue.use(BootstrapVue)
Vue.use(Datatable)
Vue.use(PrettyCheckbox)
Vue.use(VueMoment)
Vue.use(VuejsDialog)

Vue.use(VueGoogleMaps, {
  installComponents: true,
  load: {
    key: process.env.GMAPS_KEY || ''
  }
})

Vue.use(VueResource)
Vue.http.options.root = endpoints.baseUrl

// Add a response interceptor
Vue.http.interceptors.push(function (request, next) {
  next(function (res) {
    if (res.status !== 200) {
      var text = res.statusText ? res.statusText : 'Server Error'

      if (res.status === 502 || res.status === 0) {
        text = 'Error reaching server'
      }

      if (_.isObject(res.body) && res.body.message) {
        text = res.body.message
      }

      // Only bounce the user out to the login screen if it was
      // an error logging in, refreshing, or fetching user data
      if (_.includes(request.url, 'auth/')) {
        store.commit('RESET_USER_CONTEXT')
        store.resetState()

        if (_.includes(request.url, 'auth/refresh')) {
          Vue.auth.logout()
          text = 'Token expired. Please login again.'
        }

        store.commit(
          'setLoginApiError',
          Object.assign(res.body, {
            message: text
          })
        )

        // If we fail to make our logout request because the server isn't available, logout again, but without making the auth/logout call
        if (res.status === 0 && request.url === 'auth/logout') {
          Vue.auth.logout({
            makeRequest: false
          })
        }
      }
    }

    // Unauthorized Access
    if (res.status === 401 && ['UnauthorizedAccess', 'InvalidToken'].indexOf(res.data.code) > -1) {
      Vue.auth.logout()
    }
  })
})

const moment = require('moment')

/* eslint-disable no-new */
Vue.filter('unixToHuman', function (value) {
  if (!value) {
    return ''
  }

  return moment.unix(value).format('MM/DD/YYYY hh:mm')
})

// Adding global instance properties
// https://vuejs.org/v2/cookbook/adding-instance-properties.html
Vue.prototype.$utils = utils
Vue.prototype._ = _

Vue.prototype.$dateFormatter = function (date) {
  return moment(date).format('MM/DD/YYYY')
}

// Augmenting our store with a reset all
store.resetState = resetState

// https:// github.com/websanova/vue-auth/blob/master/docs/Options.md
Vue.router = router // Required binding by vue-auth library
Vue.use(vueAuth, {
  auth: vueAuthBearer,
  http: vueAuthHttp,
  router: vueAuthRouter,
  loginData: {
    method: 'POST',
    redirect: { path: '/' },
    fetchUser: true
  },
  logoutData: {
    'method': 'POST',
    redirect: '/login',
    makeRequest: true
  },
  // The default parseUserData returns `data.data`, we just need `data`
  parseUserData: function (data) {
    return data
  },
  fetchData: {
    success: (res) => {
      store.dispatch('initializeUser', res.body)
    }
  }
})

Vue.use(VeeValidate, {
  errorBagName: 'inputValidationErrors',
  fieldsBagName: 'inputValidationFields',
  events: 'change'
})

Vue.use(Vuex)

Vue.config.productionTip = true

// https://vuejs.org/v2/api/#errorHandler
// TO DO - Add in Sentry or Bugsnag integration for live logging
Vue.config.errorHandler = function (err, vm, info) {
  // handle error
  // `info` is a Vue-specific error info, e.g. which lifecycle hook
  // the error was found in. Only available in 2.2.0+
  console.log('Unhandled error:')
  console.log(err)
  console.log(info)
}

new Vue({
  el: '#app',
  router,
  store,
  template: '<App/>',
  components: {
    App
  }
})



// WEBPACK FOOTER //
// ./src/main.js